import React, {useState, useEffect} from 'react'
import {
    Box,
    Text,
    Icon,
    Flex,
    Grid,
    Skeleton,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import WebDrawer from '../web-drawer'
import {useForm} from 'react-hook-form'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import PropTypes from 'prop-types'

/* Components */
import NotifyMeFields from '../forms/notify-me-fields'
import ProductTile from '../product-tile'

/* Hooks */
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useProductsSimilars} from '../../integrations/vickyai/hooks/use-similars'
import {useEnrichVickyProducts} from '../../hooks/use-enrich-vicky-products'
import {notifyMe} from '../../integrations/notifyme/hooks/use-notify-me'
import {useNewsletter} from '../../integrations/newsletter/hooks/use-newsletter'

/* Icons */
import {ConfirmationIcon, NotifyMeReturnIcon} from '../icons/index'

/* Mocked Data */
const useMockedData = false; // This must be set back to false after the demo.
import {vickyAIMockedData} from './vickyAIMockedData'

import {sendNotifyMeEvent} from '../../utils/gtm-utils'
import {resolveGDIStyles, GDI_TEXT_BLOCK_FIELD_MAP} from '../optimizely/gdi/utils/gdi-styles'
import {renderLayoutsSwitch} from '../optimizely/utils/contentRenderer'
import {GDIDynamicContentProvider} from '../optimizely/gdi/utils/gdi-dynamic-content-context'
import {useBrand} from '../../contexts/brand-context'

const messages = defineMessages({
    notifyMeError: {
        defaultMessage: 'Failed to submit data',
        id: 'pdp_notify_me_general.error'
    },
    waitlistTitle: {
        defaultMessage: 'Waitlist',
        id: 'pdp_notify_me_modal.title'
    },
    emailValidationError: {
        defaultMessage: 'Please enter a valid email address',
        id: 'email.validation.error'
    }
})

const NotifyMeSuccess = ({
    product,
    variant,
    onRetry
}) => {
    const styles = useMultiStyleConfig('NotifyMe')
    const [size, setSize] = useState(null)
    const [color, setColor] = useState(null)
    const [length, setLength] = useState(null)

    useEffect(() => {
        if (product?.variationValues && product?.variationAttributes) {
            const sizeAttribute = product.variationAttributes.find(attr => attr.id === 'size')
            const colorAttribute = product.variationAttributes.find(attr => attr.id === 'color')
            const lengthAttribute = product.variationAttributes.find(attr => attr.id === 'length')
            if (sizeAttribute) {
                setSize(sizeAttribute.values.find(v => v.value === product?.variationValues?.size)?.name)
            }
            if (colorAttribute) {
                setColor(colorAttribute.values.find(v => v.value === product?.variationValues?.color)?.name)
            }
            if (lengthAttribute) {
                setLength(lengthAttribute.values.find(v => v.value === product?.variationValues?.length)?.name)
            }
        }
    }, [product?.variationValues])

    const sizeName = variant?.variationValues?.size || size
    const colorName = variant?.variationValues?.color
        ? product?.variationAttributes?.find(a => a.id === 'color')?.values?.find(v => v.value === variant.variationValues.color)?.name
        : color
    const lengthName = variant?.variationValues?.length || length

    const variantDetails = [colorName, sizeName, lengthName].filter(Boolean).join(', ')

    return (
        <Box>
            <Text as="h3" textStyle="webH3">
                <FormattedMessage
                    defaultMessage="Notify me when it's back"
                    id="pdp_notify_me_modal_success.heading"
                />
            </Text>
            <Flex {...styles.successTextFlex}>
                <Icon as={ConfirmationIcon} {...styles.successIcon} />
                <Text {...styles.successText}>
                    <FormattedMessage
                        defaultMessage="Thank you! We will notify you when {productName}in {variantDetails} will be back in stock."
                        id="pdp_notify_me_modal_success.subheading"
                        values={{
                            productName: product?.name ? `${product.name} ` : '',
                            variantDetails: variantDetails || ''
                        }}
                    />
                </Text>
            </Flex>
            <Flex {...styles.retryFlex}>
                <Icon as={NotifyMeReturnIcon} {...styles.retryIcon} />
                <Box as="button" onClick={onRetry} {...styles.retryButton}>
                    <Text textStyle="labelXs">
                        <FormattedMessage
                            defaultMessage="Subscribe again"
                            id="pdp_notify_me_modal_success.retry"
                        />
                    </Text>
                </Box>
            </Flex>
        </Box>
    )
}

const NotifyMeModal = ({isOpen, onClose, product, variant, showSuccessContent, notifyMeDrawerTitle, notifyMeRecommendation, subscriptionSource}) => {
    const styles = useMultiStyleConfig('NotifyMe')
    const {data: customer} = useCurrentCustomer()
    const [error, setError] = useState(null)
    const [successContent, setSuccessContent] = useState(false)
    const [newsletterData, setNewsletterData] = useState({
        notifyMeTermsAndConditions: false,
        email: null,
        type: null,
        source: null
    })
    const [productId, setProductId] = useState(variant?.productId || product?.id)
    const intl = useIntl()
    const {isGarage} = useBrand()
    const form = useForm({
        mode: 'onSubmit'
    })

    // Only fetch similars when modal is open (lazy load)
    let {recommendations, isLoading: isSimilarsLoading} = useProductsSimilars(productId, {enabled: isOpen})
    const {isLoading, submitNotifyMe} = notifyMe()
    const {submitNewsletter} = useNewsletter({...newsletterData})

    if (useMockedData) {
        recommendations = vickyAIMockedData;
    }

    /* Vicky returns variationGroup-shaped items missing per-color image
     * groups + full variants. Reshape against the master so the tile gets
     * proper swatches / hero swap / sizing / QuickView, identical to PLP. */
    const enrichedRecommendations = useEnrichVickyProducts(
        recommendations?.data,
        {enabled: isOpen}
    )

    useEffect(() => {
        setProductId(variant?.productId || product?.id)
    }, [variant?.productId, product?.id])

    const onSubmit = async (data) => {
        try {
            const notifyMeResponse = await submitNotifyMe({
                formData: JSON.stringify(data),
                ...(subscriptionSource && {subscriptionSource})
            })

            setNewsletterData({
                email: data.notifyMeEmail,
                type: 'Web',
                source: 'NotifyMe',
                notifyMeTermsAndConditions: data.notifyMeTermsAndConditions
            })

            if (notifyMeResponse && notifyMeResponse?.requestId && notifyMeResponse?.resultMessages.length === 0) {
                // GTM Modification
                sendNotifyMeEvent(variant || product)
                setError(null)
                setSuccessContent(true)
            } else if (notifyMeResponse && notifyMeResponse.emailInvalid) {
                setError(intl.formatMessage(messages.emailValidationError))
                setSuccessContent(false)
            } else {
                setError(intl.formatMessage(messages.notifyMeError))
                setSuccessContent(false)
            }
        } catch (error) {
            setError(error.message)
        }
    }

    useEffect(() => {
        if (newsletterData?.notifyMeTermsAndConditions) {
            submitNewsletter()
        }
    }, [newsletterData?.notifyMeTermsAndConditions])

    const handleClose = () => {
        form.reset()
        setSuccessContent(false)
        onClose()
    }

    const handleSubmit = form.handleSubmit(onSubmit)

    const cmsTitle = notifyMeDrawerTitle?.TextForWeb
        ? notifyMeDrawerTitle.TextForWeb.replace(/<[^>]*>/g, '').trim()
        : null
    const {styles: cmsTitleStyles} = resolveGDIStyles(
        notifyMeDrawerTitle?.GDITextBlockStylesPerViewType,
        GDI_TEXT_BLOCK_FIELD_MAP
    )
    const cmsTitleSx = Object.keys(cmsTitleStyles).length > 0 ? cmsTitleStyles : null

    useEffect(() => {
        if (showSuccessContent && customer?.isRegistered) {
            setSuccessContent(true)
        }
    }, [showSuccessContent])

    return (
        <WebDrawer
            isOpen={isOpen}
            onClose={handleClose}
            title={cmsTitle || intl.formatMessage(messages.waitlistTitle)}
            titleSx={cmsTitleSx}
            closeOnOverlayClick={true}
            size="full"
        >
            {!successContent && (
                <Box>
                    <Text as="h2" mb={4} textStyle="webH3">
                        <FormattedMessage
                            defaultMessage="Notify me when it's back"
                            id="pdp_notify_me_modal.heading"
                        />
                    </Text>
                    <form
                        onSubmit={(event) => {
                            event.stopPropagation()
                            handleSubmit(event)
                        }}
                        noValidate
                    >
                        <NotifyMeFields
                            form={form}
                            sku={productId}
                            defaultEmail={customer?.isRegistered ? customer.email : ''}
                            isEmailReadOnly={customer?.isRegistered}
                            isLoading={isLoading}
                            onEmailChange={() => {
                                if (error) {
                                    setError(null)
                                }
                            }}
                        />

                        {error && <Text {...styles.errorText}>{error}</Text>}
                    </form>
                </Box>
            )}

            {successContent && (
                <Box>
                    <NotifyMeSuccess
                        product={product}
                        variant={variant}
                        onRetry={() => setSuccessContent(false)}
                    />
                </Box>
            )}

            {isSimilarsLoading ? (
                <Box {...styles.similarBox}>
                    <Skeleton height="24px" width="60%" mb={2} />
                    <Skeleton height="16px" width="80%" mb={4} />
                    <Grid {...styles.similarGrid}>
                        {[...Array(4)].map((_, index) => (
                            <Box key={`notify-me-skeleton-${index}`} {...styles.similarItem}>
                                <Skeleton height={250} />
                                <Skeleton count={2} />
                            </Box>
                        ))}
                    </Grid>
                </Box>
            ) : notifyMeRecommendation?.length > 0 ? (
                /* Break the recommendations out of the DrawerBody's horizontal
                 * padding (webDrawer theme `body.paddingLeft/Right: 4`) so the
                 * carousel spans the full drawer width. Any left/right gutter
                 * is then controlled by the CMS block padding/margin (peek). */
                <Box {...styles.similarBox} mx={{base: -4, lg: -4}}>
                    <GDIDynamicContentProvider
                        value={{
                            contentPageType: 'GDIPDPPage',
                            pdpProduct: variant || product,
                            pdpProductSku: variant?.productId || product?.id,
                            disableViewportBleed: true,
                            carouselPeekInset: true
                        }}
                    >
                        {notifyMeRecommendation.map((item, index) => (
                            <React.Fragment
                                key={
                                    item?.ContentLink?.GuidValue ||
                                    item?.ContentLink?.Id ||
                                    `notify-me-recommendation-${index}`
                                }
                            >
                                {renderLayoutsSwitch(item?.ContentLink?.Expanded)}
                            </React.Fragment>
                        ))}
                    </GDIDynamicContentProvider>
                </Box>
            ) : (
                enrichedRecommendations?.length > 0 && (
                    <Box {...styles.similarBox}>
                        <Text as="h3" textStyle="webH3" mb={2}>
                            {isGarage ? (
                                <FormattedMessage
                                    defaultMessage="Shop Similar Styles"
                                    id="pdp_notify_me_modal_similar.heading.garage"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Shop Similar Styles"
                                    id="pdp_notify_me_modal_similar.heading.dynamite"
                                />
                            )}
                        </Text>
                        <Text textStyle="bodyXsRegular">
                            {isGarage ? (
                                <FormattedMessage
                                    defaultMessage="Need it now? Try these similar styles in size [{size}]."
                                    id="pdp_notify_me_modal_similar.subheading.garage"
                                    values={{
                                        size:
                                            product?.variationAttributes
                                                ?.find((a) => a.id === 'size')
                                                ?.values?.find(
                                                    (v) =>
                                                        v.value ===
                                                        (variant?.variationValues?.size ||
                                                            product?.variationValues?.size)
                                                )?.name ||
                                            variant?.variationValues?.size ||
                                            product?.variationValues?.size ||
                                            ''
                                    }}
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Need it now? Try these similar styles in size [{size}]."
                                    id="pdp_notify_me_modal_similar.subheading.dynamite"
                                    values={{
                                        size:
                                            product?.variationAttributes
                                                ?.find((a) => a.id === 'size')
                                                ?.values?.find(
                                                    (v) =>
                                                        v.value ===
                                                        (variant?.variationValues?.size ||
                                                            product?.variationValues?.size)
                                                )?.name ||
                                            variant?.variationValues?.size ||
                                            product?.variationValues?.size ||
                                            ''
                                    }}
                                />
                            )}
                        </Text>

                        <Box {...styles.similarGrid}>
                            {enrichedRecommendations?.slice(0, 4).map((recommendation, index) => {
                                return (
                                    <Box
                                        key={`shop-similar-notify-me-${index}`}
                                        {...styles.similarItem}
                                    >
                                        <ProductTile
                                            product={recommendation}
                                            showQV={true}
                                            enableFavourite={true}
                                        />
                                    </Box>
                                )
                            })}
                        </Box>
                    </Box>
                )
            )}
        </WebDrawer>
    )
}

NotifyMeModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    product: PropTypes.object,
    variant: PropTypes.object,
    showSuccessContent: PropTypes.bool,
    notifyMeDrawerTitle: PropTypes.object,
    notifyMeRecommendation: PropTypes.array,
    subscriptionSource: PropTypes.string
}

export default NotifyMeModal
